import { fetchCategorySharedData, fetchCategoryLocaleData } from './dataApi';

export const VOCABULARY_ROOTS = {
  thematic: 'thematic-vocabulary',
  wordclass: 'wordclass-vocabulary',
};

const topicsPromises = new Map();

// The topic/group catalog (id/label/icon/order) is identical no matter which
// curriculum home it's requested for, so it lives once at
// data-common/{root}/_topics.json instead of duplicated under every home.
export function loadVocabularyCatalog(homeLang) {
  if (!topicsPromises.has(homeLang)) {
    topicsPromises.set(homeLang, Promise.all([
      fetchCategorySharedData(VOCABULARY_ROOTS.thematic, '_topics.json'),
      fetchCategorySharedData(VOCABULARY_ROOTS.wordclass, '_topics.json'),
    ]).then(([thematic, wordclass]) => ({
      thematic: thematic.topics || [],
      wordclass: wordclass.topics || [],
    })).catch((error) => {
      topicsPromises.delete(homeLang);
      throw error;
    }));
  }
  return topicsPromises.get(homeLang);
}

function overlayById(sourceItems = [], overlayItems = []) {
  const overlays = new Map(overlayItems.map((item) => [item.id, item]));
  return sourceItems.map((item) => ({ ...item, ...(overlays.get(item.id) || {}) }));
}

export function mergeVocabularyOverlay(source, overlay, locale) {
  const overlaySections = new Map((overlay?.sections || []).map((section) => [section.id, section]));
  const sections = (source.sections || []).map((section) => {
    const localized = overlaySections.get(section.id);
    return {
      ...section,
      ...localized,
      items: overlayById(section.items, localized?.items),
    };
  });

  const content = {};
  sections.forEach((section) => {
    section.items.forEach((item) => {
      content[item.id] = { ...item, sectionId: section.id, sectionLabel: section.label };
    });
  });

  const topic = { ...(source.topic || {}), ...(overlay?.topic || {}) };

  return {
    schemaVersion: 2,
    locale,
    id: topic.id,
    label: topic.label || topic.id || '',
    icon: topic.icon || '',
    topic,
    sections,
    content,
  };
}

// EN's per-topic document is the rich, locale-independent skeleton — item
// order/level/emoji/partOfSpeech/grammarRole — that every other locale
// overlays its own item text onto (the source data already documents this
// as EN being the canonical shape). It lives at data-common/{root}/en/
// {topicId}.json (see _topics.json's own `file` field for each topic — this
// is that same path) instead of one shared _skeleton.json holding every
// topic at once.
const VOCABULARY_ANCHOR_HOME = 'en';
const skeletonTopicPromises = new Map();

function fetchSkeletonTopic(root, topicId, options) {
  const key = `${root}/${topicId}`;
  if (!skeletonTopicPromises.has(key)) {
    skeletonTopicPromises.set(
      key,
      fetchCategoryLocaleData(root, VOCABULARY_ANCHOR_HOME, `${topicId}.json`, options).catch((error) => {
        skeletonTopicPromises.delete(key);
        throw error;
      }),
    );
  }
  return skeletonTopicPromises.get(key);
}

// Rebuilds a {topic, sections} overlay from a flat id->text map, using the
// English source's section/item structure as the skeleton (ids are aligned
// 1:1 across every locale, verified at migration time). Note: a small
// number of item ids recur across multiple topics with slightly different
// wording per context (~0.5% of thematic-vocabulary's items, e.g. German
// "check_in" as noun vs verb) — the flat map can only hold one value per
// id, so those items show the same text regardless of which topic they're
// viewed in. Accepted as a minor, documented limitation.
function overlayFromFlatText(enCategory, flatText) {
  return {
    topic: { id: enCategory.id },
    sections: enCategory.sections.map((section) => ({
      id: section.id,
      items: section.items
        .filter((item) => flatText[item.id] !== undefined)
        .map((item) => ({ id: item.id, text: flatText[item.id] })),
    })),
  };
}

// Resolves one code's item-text overlay from data-common's shared
// {root}/{code}/{topicId}.json flat-text lookup (populated for all ~40
// codes, including every curriculum home — the per-home consolidated
// document never carried anything beyond {id, text} for item lookups, so
// the flat map already covers it; see loadVocabularySectionLessonCards for
// the one thing a home's own document *does* carry beyond that: `.lesson`
// cards).
async function resolveVocabularyOverlay(root, enCategory, code, options) {
  if (code === VOCABULARY_ANCHOR_HOME) return null;
  const flatText = await fetchCategoryLocaleData(root, code, `${enCategory.id}.json`, options).catch(() => ({}));
  return overlayFromFlatText(enCategory, flatText);
}

export async function loadConsolidatedVocabularyTopic(type, homeLang, nativeCode, learnCode, topicId, options) {
  const root = VOCABULARY_ROOTS[type];
  if (!root) throw new Error(`Unknown vocabulary type: ${type}`);
  const enCategory = await fetchSkeletonTopic(root, topicId, options);
  const source = { topic: { id: topicId }, sections: enCategory.sections };

  const [nativeOverlay, learnOverlay] = await Promise.all([
    resolveVocabularyOverlay(root, enCategory, nativeCode, options),
    resolveVocabularyOverlay(root, enCategory, learnCode, options),
  ]);

  return {
    reader: mergeVocabularyOverlay(source, nativeOverlay, nativeCode),
    target: mergeVocabularyOverlay(source, learnOverlay, learnCode),
  };
}

function sortedLessonCards(lesson) {
  if (!lesson || typeof lesson !== 'object') return [];
  return Object.entries(lesson)
    .filter(([key, item]) => /^\d+$/.test(key) && item && typeof item === 'object')
    .sort(([first], [second]) => Number(first) - Number(second))
    .map(([, item]) => item);
}

// Each vocabulary section's `lesson` field (added alongside the existing
// `items` array — never touched, since Practice mode, Search, and offline
// packs on both platforms still read `items` directly) holds the same
// numbered-key/`type`-tagged card format topic-lessons/grammar use — an
// additional interactive entry point; the reference card/list view stays
// unchanged. Unlike item text, `.lesson` cards are genuinely authored per
// curriculum home (not a text-only overlay onto the EN skeleton), so English
// reads straight from its own per-topic skeleton file while every other home
// reads its own per-topic document (data-common/{root}/{homeLang}/document/
// {categoryId}.json) — the one piece of a home's curriculum the flat text
// overlay didn't capture.
export async function loadVocabularySectionLessonCards(type, homeLang, categoryId, sectionId) {
  const root = VOCABULARY_ROOTS[type];
  if (!root) throw new Error(`Unknown vocabulary type: ${type}`);
  const category = homeLang === VOCABULARY_ANCHOR_HOME
    ? await fetchSkeletonTopic(root, categoryId)
    : await fetchCategoryLocaleData(root, homeLang, `document/${categoryId}.json`);
  const section = (category.sections || []).find((s) => s.id === sectionId);
  if (!section) throw new Error(`Unknown ${root} section: ${sectionId}`);
  const items = sortedLessonCards(section.lesson);
  const chapter = { id: sectionId, label: section.label || section.id };
  return { chapter, chapters: [chapter], items };
}

export function clearVocabularyCatalogCache() {
  topicsPromises.clear();
  skeletonTopicPromises.clear();
}
